/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/camelcase */
import { createSelector } from 'reselect';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { getCardPaymentsForIntegration } from 'reducers/Payments';
import { deletePayment, markForProcessing } from 'actions/Payments';
import { isEmpty } from 'utils';
import { getCafaEntry } from 'reducers/cafaConfigs';
import { CardPaymentHooks, FUNC_BUTTONS } from 'paymentIntegrations/types';
import { addError } from 'actions/Error';
import { openTerminalIntegration } from 'actions/integrations/terminal';
import { RootState } from 'reducers';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { getSelectedPosID } from 'reducers/PointsOfSale';
import { getCompany } from 'reducers/Login';

import { baseLog, integrationName, integrationType } from './constants';
import {
  cancelCurrentReaderAction,
  processPayments,
} from './handlers/processPayments';
import { cancelPaymentIntent } from './API';
import { Configuration } from './types';

/**
 * Sanitize the `statement_descriptor` field per stripe documentation
 * @see https://docs.stripe.com/payments/payment-intents#dynamic-statement-descriptor
 */
function trimDescriptor(text: string) {
  let result = text;
  // Statement descriptors are limited to 22 characters,
  let LIMIT = 22
  // can’t use the special characters <, >, ', ", or *,
  result = result.replace(/[<>'"*]/g, '')
  // and must not consist solely of numbers.
  if (!/\D/.test(result)) {
    result = `C${result}`
  }

  // When using dynamic statement descriptors,
  // the dynamic text is appended to the statement descriptor prefix set in the Stripe Dashboard.
  // An asterisk (*) and an empty space are also added to separate
  // the default statement descriptor from the dynamic portion.
  // These 2 characters count towards the 22 character limit.
  LIMIT -= 2

  if (result.length > LIMIT) {
    result = result.slice(0, LIMIT-3);
    result += '...'
  }
  return result;
}

export const getStripeConfiguration = createSelector(
  getCafaEntry(integrationName, integrationType),
  (e): undefined | Configuration => e?.value ?? {},
);

export const getRequestHeaders = createSelector(
  getStripeConfiguration,
  conf => ({
    'Content-Type': 'application/x-www-form-urlencoded',
    Authorization: `Bearer ${conf?.secret}`,
  }),
);
export const getAttachRequestMetadata = createSelector(
  state => getCompany(state),
  state => getSelectedWarehouseID(state),
  state => getSelectedPosID(state),
  ({ name: companyName }, warehouseID, posID) =>
    function attachRequestMetadata(request) {
      return {
        ...request,
        statement_descriptor: trimDescriptor(companyName),
        // This is the format expected when sending x-www-form-urlencoded which stripe requires
        // Ideally we'd return a normal nested object here and transform it where the request is made
        // but it's a lot simpler to just specify the keys in this format right now
        "metadata[payment_source]": 'Erply',
        "metadata[location_id]": warehouseID,
        "metadata[pos_id]": posID,
      };
    },
);

export const voidPayments = () => async (dispatch, getState) => {
  const log = baseLog.extend('voidPayments');
  const integrationCardPayments = getCardPaymentsForIntegration('stripe')(
    getState(),
  );
  log(
    'Void payments called for the following payments: ',
    integrationCardPayments,
  );

  integrationCardPayments.forEach(({ key, paid }) =>
    paid
      ? dispatch(markForProcessing({ key }))
      : dispatch(deletePayment({ key })),
  );
  const finalPayments = getCardPaymentsForIntegration('stripe')(getState());
  log(
    'Some payments were marked for processing, while some were deleted. Final payments: ',
    finalPayments,
  );
  if (finalPayments.length) {
    log('Final payments exist. Opening terminal integration.');
    await dispatch(openTerminalIntegration());
  }
};

export const initPayments = (params: CardPaymentHooks) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const state = getState();

  const {
    enableButtons,
    rejectPayments,
    resolvePayments,
    displayError,
  } = params;

  const config = getStripeConfiguration(state);

  if (isEmpty(config)) {
    displayError('Stripe not configured.');
    rejectPayments();
    return;
  }
  enableButtons([]);

  dispatch(processPayments(params))
    .then(async ({ errors }: { errors: string[] }) => {
      const cardPayments = getCardPaymentsForIntegration('stripe')(getState());
      const shouldVoid = cardPayments.every(p => p.shouldProcess && p.paid);

      if (errors.length) {
        enableButtons([FUNC_BUTTONS.RETRY, FUNC_BUTTONS.CANCEL]);
      } else if (shouldVoid) {
        // Before rejecting need to map through the payments and cancel all payments that have
        await Promise.all(
          cardPayments
            .filter(c => c.attributes.stripePaymentId)
            .map(async d => {
              try {
                const result = await dispatch(
                  cancelPaymentIntent({
                    payment_intent: d.attributes.stripePaymentId,
                    reason: 'requested_by_customer',
                  }),
                );
                if (result) {
                  dispatch(deletePayment({ key: d.key }));
                }
                return result;
              } catch (e) {
                if (e instanceof Error) {
                  params.updateMessage(e.message);
                } else if (typeof e === 'string') {
                  params.updateMessage(e);
                }
                throw e;
              }
            }),
        );
        rejectPayments();
      } else {
        resolvePayments();
      }
    })
    .catch(error => {
      console.error('Failed to process payment with Stripe integration', error);
      displayError(
        `Failed to process payment with Stripe integration: ${error}`,
      );
      enableButtons([FUNC_BUTTONS.RETRY, FUNC_BUTTONS.CANCEL]);
      throw error;
    });
};

const cancelCurrentPayment = ({
  enableButtons,
  rejectPayments,
}: CardPaymentHooks) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
) => {
  await dispatch(cancelCurrentReaderAction());
  enableButtons([]);
  rejectPayments();
};

export const retryPayments = (params: CardPaymentHooks) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const cardPayments = getCardPaymentsForIntegration('stripe')(getState());
  const needToVoid = cardPayments.every(c => c.paid);
  if (needToVoid) {
    cardPayments.forEach(c => dispatch(markForProcessing({ key: c.key })));
  }
  const cardPaymentsToRetry = getCardPaymentsForIntegration('stripe')(
    getState(),
  );
  return dispatch(
    initPayments({ ...params, cardPayments: cardPaymentsToRetry }),
  );
};

export const functions = [
  {
    actionOnClick: cancelCurrentPayment,
    text: 'Cancel',
    name: FUNC_BUTTONS.CANCEL,
    variant: 'danger',
  },
  {
    actionOnClick: retryPayments,
    text: 'Retry',
    name: FUNC_BUTTONS.RETRY,
    variant: 'warning',
  },
];
